// Design system for Thought Log application
// Inspired by minimalist Apple and Stripe design principles

// Colors
export const colors = {
    // Primary palette
    primary: "#71B7FF", // Brighter blue for higher contrast
    primaryDark: "#5AA2EB",
    primaryLight: "#8EC5FF",

    // Grays - High contrast dark theme
    background: "#0C0C0F", // Very dark background (almost black) like Linear
    surfaceElevated: "#18181B", // Increased contrast with background
    surface: "#27272B", // Higher contrast surface color
    divider: "#383843", // More distinct dividers

    // Text - Higher contrast
    textPrimary: "#FFFFFF", // Pure white for primary text
    textSecondary: "#C2C2C7", // Lighter gray for better visibility
    textTertiary: "#8E8E99", // Brighter tertiary text
    textDisabled: "#5C5C64", // Lighter disabled text

    // Status - More vibrant for grayscale visibility
    success: "#4CD964", // Brighter green
    error: "#FF453A", // Vibrant red
    warning: "#FFD60A", // Brighter yellow

    // Tag colors - Higher contrast
    tagBackground: "#27272B",
    tagText: "#FFFFFF",

    // Accent colors for UI elements - More vibrant
    accent: {
        white: "#ffffff", // Brighter blue
        purple: "#B87FFF", // Vibrant purple
        indigo: "#7E9CFF", // Brighter indigo
        violet: "#712AC9",
    }
};

// Constants for the app
export const CONSTANTS = {
    MAX_DROPDOWN_ITEMS: 50,
    MAX_TAGS: 10,
};

// Typography
export const typography = {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    fontWeightRegular: "400",
    fontWeightMedium: "500",
    fontWeightBold: "600",

    h1: {
        fontSize: "32px",
        lineHeight: "40px",
        fontWeight: "600",
        letterSpacing: "-0.3px",
    },
    h2: {
        fontSize: "24px",
        lineHeight: "32px",
        fontWeight: "600",
        letterSpacing: "-0.2px",
    },
    h3: {
        fontSize: "18px",
        lineHeight: "28px",
        fontWeight: "600",
        letterSpacing: "-0.1px",
    },
    body: {
        fontSize: "16px",
        lineHeight: "24px",
        fontWeight: "400",
    },
    label: {
        fontSize: "14px",
        lineHeight: "20px",
        fontWeight: "500",
    },
    caption: {
        fontSize: "12px",
        lineHeight: "16px",
        fontWeight: "400",
    }
};

// Spacing system based on 4px increments
export const spacing = {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
    xxl: "48px",
};

// Shadows
export const shadows = {
    sm: "0 1px 3px rgba(0,0,0,0.08)",
    md: "0 4px 12px rgba(0,0,0,0.05)",
    lg: "0 12px 24px rgba(0,0,0,0.07)",
    focus: "0 0 0 4px rgba(0, 122, 255, 0.3)", // Blue focus ring
};

// Transition for animations
export const transitions = {
    default: "all 200ms ease-in-out",
    fast: "all 100ms ease-in-out",
    slow: "all 300ms ease-in-out",
};

// Border radius
export const borderRadius = {
    sm: "6px",
    md: "8px",
    lg: "12px",
    xl: "16px",
    circle: "50%",
};

// Component-specific styles
export const styles = {
    spacing,
    typography,
    borderRadius,
    transitions,
    
    // Layout containers
    container: {
        maxWidth: "1000px",
        margin: "0 auto",
        padding: spacing.xl,
        fontFamily: typography.fontFamily,
        color: colors.textPrimary,
        backgroundColor: colors.background,
    },

    pageHeader: {
        marginBottom: spacing.xl,
    },
    
    // Standard page header container used across pages
    pageHeaderContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: spacing.md,
        padding: `0 ${spacing.md}`,
        height: '38px'
    },

    // Renamed to better indicate this is specifically for desktop layouts
    desktopUtilityBarContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        maxWidth: '500px',
        width: '60%',
        height: '38px'
    },

    // Mobile-specific utility bar container
    mobileUtilityBarContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        flex: 1,
        height: '38px'
    },

    // Legacy name kept for backward compatibility
    utilityBarContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        maxWidth: '500px',
        width: '60%',
        height: '38px'
    },

    // Tabs
    tabs: {
        display: "flex",
        marginBottom: spacing.xl,
        borderRadius: borderRadius.lg,
        padding: spacing.xs,
        backgroundColor: colors.surfaceElevated,
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        overflow: "hidden",
    },

    tab: (isActive) => ({
        flex: 1,
        padding: `${spacing.md} ${spacing.lg}`,
        textAlign: "center",
        cursor: "pointer",
        fontWeight: isActive ? typography.fontWeightBold : typography.fontWeightMedium,
        fontSize: typography.label.fontSize,
        lineHeight: typography.label.lineHeight,
        color: isActive ? colors.primary : colors.textSecondary,
        backgroundColor: isActive ? colors.surface : "transparent",
        borderRadius: borderRadius.md,
        transition: transitions.default,
        border: "none",
        boxShadow: isActive ? "0 2px 8px rgba(0,0,0,0.2)" : "none",
        transform: isActive ? "translateY(0)" : "translateY(0)",
        "&:hover": {
            backgroundColor: isActive ? colors.surface : colors.surfaceElevated,
            color: isActive ? colors.primary : colors.textPrimary,
        },
    }),

    // Form components
    formGroup: {
        marginBottom: spacing.lg,
    },

    label: {
        display: "block",
        marginBottom: spacing.sm,
        fontWeight: typography.fontWeightMedium,
        fontSize: typography.label.fontSize,
        color: colors.textPrimary,
    },

    input: {
        width: "100%",
        padding: spacing.md,
        fontSize: typography.body.fontSize,
        lineHeight: typography.body.lineHeight,
        color: colors.textPrimary,
        backgroundColor: colors.surface,
        borderRadius: borderRadius.md,
        border: `1px solid ${colors.divider}`,
        boxSizing: "border-box",
        transition: transitions.default,
        boxShadow: "none",
        outline: "none",
        "&:focus": {
            borderColor: colors.primary,
            boxShadow: "0 0 0 2px rgba(113, 183, 255, 0.4)",
        },
        "&:hover:not(:focus)": {
            borderColor: colors.textTertiary,
        },
        "&:disabled": {
            backgroundColor: colors.surfaceElevated,
            color: colors.textDisabled,
            cursor: "not-allowed",
        },
    },

    // Autocomplete components
    autocomplete: {
        border: `1px solid ${colors.divider}`,
        borderRadius: borderRadius.md,
        listStyle: "none",
        margin: 0,
        padding: 0,
        position: "absolute",
        backgroundColor: colors.surface,
        width: "100%",
        zIndex: 100,
        maxHeight: "240px",
        overflowY: "auto",
        boxShadow: shadows.md,
        top: "calc(100% + 4px)",
        left: 0,
    },

    autocompleteItem: {
        padding: spacing.md,
        cursor: "pointer",
        fontSize: typography.body.fontSize,
        lineHeight: typography.body.lineHeight,
        color: colors.textPrimary,
        borderBottom: `1px solid ${colors.divider}`,
        transition: transitions.fast,
        "&:last-child": {
            borderBottom: "none",
        },
        "&:hover": {
            backgroundColor: colors.surfaceElevated,
        },
    },

    // Tag chips
    tagChip: {
        display: "inline-flex",
        alignItems: "center",
        padding: `${spacing.xs} ${spacing.sm}`,
        margin: spacing.xs,
        backgroundColor: colors.tagBackground,
        color: colors.tagText,
        borderRadius: "16px",
        fontSize: typography.caption.fontSize,
        fontWeight: typography.fontWeightMedium,
        boxShadow: shadows.sm,
        transition: transitions.default,
        cursor: "default",
        "&:hover": {
            backgroundColor: "#EBF0F5",
        },
    },

    chipRemove: {
        marginLeft: spacing.xs,
        fontWeight: typography.fontWeightBold,
        color: colors.textTertiary,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "18px",
        height: "18px",
        borderRadius: borderRadius.circle,
        transition: transitions.fast,
        "&:hover": {
            color: colors.error,
            backgroundColor: "rgba(255, 69, 58, 0.1)",
        },
    },

    // Buttons
    button: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: `${spacing.sm} ${spacing.lg}`,
        backgroundColor: colors.primary,
        color: "#FFFFFF",
        border: "none",
        borderRadius: "20px",
        fontWeight: typography.fontWeightMedium,
        fontSize: typography.label.fontSize,
        lineHeight: typography.label.lineHeight,
        cursor: "pointer",
        transition: transitions.default,
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        "&:hover": {
            backgroundColor: colors.primaryLight,
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        },
        "&:active": {
            backgroundColor: colors.primaryDark,
            transform: "translateY(1px)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
        },
        "&:disabled": {
            backgroundColor: colors.surfaceElevated,
            color: colors.textDisabled,
            cursor: "not-allowed",
            boxShadow: "none",
        },
    },

    // Button with white background and black text
    whiteButton: {
        backgroundColor: colors.accent.white,
        color: "#000000", // Black text on white background
        "&:hover": {
            backgroundColor: "#F2F2F2", // Slightly darker white on hover
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        },
        "&:active": {
            backgroundColor: "#E8E8E8", // Even darker white on active
            transform: "translateY(1px)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
        },
    },

    secondaryButton: {
        backgroundColor: "transparent",
        border: `1px solid ${colors.divider}`,
        color: colors.textPrimary,
        "&:hover": {
            backgroundColor: colors.surfaceElevated,
            borderColor: colors.textTertiary,
        },
    },

    dangerButton: {
        backgroundColor: colors.error,
        "&:hover": {
            backgroundColor: "#E03131",
        },
    },

    // Tables
    table: {
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: 0,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.divider}`,
        borderRadius: borderRadius.lg,
        overflow: "hidden",
        boxShadow: shadows.sm,
    },

    tableHeader: {
        padding: spacing.md,
        textAlign: "left",
        fontSize: typography.label.fontSize,
        fontWeight: typography.fontWeightMedium,
        color: colors.textSecondary,
        backgroundColor: colors.surfaceElevated,
        borderBottom: `1px solid ${colors.divider}`,
    },

    tableCell: {
        padding: spacing.md,
        fontSize: typography.body.fontSize,
        lineHeight: typography.body.lineHeight,
        color: colors.textPrimary,
        borderBottom: `1px solid ${colors.divider}`,
        transition: transitions.default,
    },

    // Empty states
    emptyState: {
        textAlign: "center",
        padding: spacing.xxl,
        color: colors.textSecondary,
    },

    // Thought card
    thoughtCard: {
        backgroundColor: colors.surfaceElevated,
        borderRadius: borderRadius.lg,
        padding: spacing.lg,
        marginBottom: spacing.md,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        border: `1px solid ${colors.divider}`,
        transition: transitions.default,
        cursor: "pointer",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            borderColor: `rgba(113, 183, 255, 0.3)`,
        },
    },

    // App header
    appHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: spacing.xl,
        paddingBottom: spacing.lg,
        borderBottom: `1px solid ${colors.divider}`,
    },

    appLogo: {
        fontSize: typography.h1.fontSize,
        fontWeight: typography.h1.fontWeight,
        color: colors.textPrimary,
        margin: 0,
        letterSpacing: "-0.5px",
    },

    // User profile display
    userProfile: {
        display: "flex",
        alignItems: "center",
        gap: spacing.md,
    },

    // Importance indicator
    importanceIndicator: (value) => {
        const getColor = () => {
            if (value <= 1) return "#E9ECEF";
            if (value <= 3) return colors.warning;
            return colors.error;
        };

        return {
            display: "inline-block",
            width: "32px",
            height: "32px",
            lineHeight: "32px",
            textAlign: "center",
            borderRadius: borderRadius.circle,
            backgroundColor: getColor(),
            color: value <= 1 ? colors.textSecondary : "#FFF",
            fontWeight: typography.fontWeightBold,
        };
    },

    // Tag display in table
    tagPill: {
        display: "inline-block",
        padding: `${spacing.xs} ${spacing.sm}`,
        margin: "0 4px 4px 0",
        backgroundColor: "rgba(113, 183, 255, 0.15)",
        color: colors.textPrimary,
        borderRadius: "12px",
        fontSize: typography.caption.fontSize,
        fontWeight: typography.fontWeightMedium,
        border: "1px solid rgba(113, 183, 255, 0.3)",
    },
    // Desktop-specific search input container
    desktopSearchInputContainer: {
        position: 'relative',
        flex: 1,
        height: '38px',
        display: 'flex',
        alignItems: 'center'
    },
    
    // Complete desktop search input component styling
    desktopSearchInput: {
        container: {
            position: 'relative',
            flex: 1,
            height: '38px',
            display: 'flex',
            alignItems: 'center'
        },
        input: {
            padding: '8px 12px 8px 36px',
            fontSize: '14px',
            borderRadius: '20px',
            backgroundColor: colors.surfaceElevated,
            border: 'none',
            color: colors.textPrimary,
            width: '100%',
            height: '32px',
        },
        icon: {
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)'
        }
    },
    
    // Mobile search input component styling
    mobileSearchInput: {
        container: {
            position: 'relative',
            flex: 1,
            height: '40px',
            display: 'flex',
            alignItems: 'center'
        },
        input: {
            padding: '8px 12px 8px 40px',
            fontSize: '16px',
            borderRadius: '20px',
            backgroundColor: colors.surfaceElevated,
            border: 'none',
            color: colors.textPrimary,
            width: '100%',
            height: '40px',
        },
        icon: {
            position: 'absolute',
            left: '14px',
            top: '50%',
            transform: 'translateY(-50%)'
        }
    },

    // Input-related components
    inputControls: {
        clearButton: {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            padding: '4px',
            cursor: 'pointer',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.textTertiary,
            zIndex: 2,
            transition: transitions.fast,
            '&:hover': {
                backgroundColor: `${colors.error}1A`, // 10% opacity error color
                color: colors.error
            }
        }
    },

    // ComposePage specific styles
    composePage: {
        mobile: {
            container: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: colors.background,
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            },
            header: {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: spacing.md,
            },
            backButton: {
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: spacing.sm,
            },
            buttonContainer: {
                display: 'flex',
                gap: spacing.sm,
            },
            addButton: {
                backgroundColor: 'transparent',
                color: colors.accent.white,
                border: `1px solid ${colors.accent.white}`,
                borderRadius: borderRadius.xl,
                padding: `${spacing.sm} ${spacing.md}`,
                fontWeight: typography.fontWeightBold,
                fontSize: typography.caption.fontSize,
            },
            saveButton: {
                backgroundColor: colors.accent.white,
                color: 'black',
                border: 'none',
                borderRadius: borderRadius.xl,
                padding: `${spacing.sm} ${spacing.md}`,
                fontWeight: typography.fontWeightBold,
                fontSize: typography.caption.fontSize,
            },
            textareaContainer: {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                overflow: 'auto',
            },
            textarea: {
                flex: 1,
                border: 'none',
                padding: spacing.md,
                fontSize: typography.h3.fontSize,
                resize: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                color: colors.textPrimary,
            },
        },
        desktop: {
            overlay: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: colors.background,
                zIndex: 999,
            },
            container: {
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '600px',
                maxWidth: '90%',
                backgroundColor: colors.surfaceElevated,
                borderRadius: borderRadius.xl,
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '80vh',
                overflow: 'hidden',
            },
            header: {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: spacing.md,
            },
            closeButton: {
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: spacing.sm,
            },
            buttonContainer: {
                display: 'flex',
                gap: spacing.sm,
            },
            addButton: {
                backgroundColor: 'transparent',
                color: colors.accent.white,
                border: `1px solid ${colors.accent.white}`,
                borderRadius: borderRadius.xl,
                padding: `${spacing.sm} ${spacing.md}`,
                fontWeight: typography.fontWeightBold,
                fontSize: typography.caption.fontSize,
            },
            saveButton: {
                backgroundColor: colors.accent.white,
                color: 'black',
                border: 'none',
                borderRadius: borderRadius.xl,
                padding: `${spacing.sm} ${spacing.md}`,
                fontWeight: typography.fontWeightBold,
                fontSize: typography.caption.fontSize,
            },
            textareaContainer: {
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '80vh',
                overflow: 'auto',
                overflowX: 'hidden',
            },
            textarea: {
                minHeight: '180px',
                border: 'none',
                padding: spacing.md,
                fontSize: typography.h3.fontSize,
                resize: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                color: colors.textPrimary,
            },
        },
        shared: {
            button: (isEnabled) => ({
                cursor: isEnabled ? 'pointer' : 'default',
                opacity: isEnabled ? 1 : 0.5,
            }),
            hideScrollbar: {
                '::-webkit-scrollbar': {
                    display: 'none',
                },
            },
        },
    },


}; 